import React, { useMemo, useState } from 'react';
import { Radio, Typography } from 'antd';
import { contentService, seasonService, seriesService } from 'content/service';
import { contractContentService } from 'contract/services/contractTermItemService';
import TransferComponent, {
  TransferDataHandlerInterface,
  TransferDataSourceInterface,
} from 'components/transfer/Transfer';
import { SeriesInterface } from 'content/type/seriesType';
import { SeasonInterface } from 'type/seasonType';
import { ContentInterface } from 'content/type/contentType';

const mappingTypes = [
  { label: 'Series', value: 'series' },
  { label: 'Season', value: 'season' },
  { label: 'Content', value: 'content' },
];

const columns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
  { title: 'Name', dataIndex: 'name', key: 'name' },
  { title: 'Type', dataIndex: 'type', key: 'type', width: 100 },
];

interface ContentsMappingProps {
  contractId: number;
  termId: number | null;
  isNewTerm?: boolean;
}

const ContentsMapping = ({ contractId: _, termId, isNewTerm = false }: ContentsMappingProps) => {
  const [mappingType, setMappingType] = useState<'series' | 'season' | 'content'>('series');

  // filterOption: 검색어 필터링 함수 (예시)
  const filterOption = (inputValue: string, option: TransferDataSourceInterface) => {
    return option.name.toLowerCase().includes(inputValue.toLowerCase());
  };

  // dataHandler: TransferComponent에서 사용할 데이터 핸들러 정의
  const dataHandler: TransferDataHandlerInterface = useMemo(
    () => ({
      fetchSourceData: async (query: Record<string, string>) => {
        let data: TransferDataSourceInterface[] = [];
        let count = 0;

        if (mappingType === 'series') {
          const res = await seriesService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: SeriesInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            disabled: false,
            type: 'series' as const,
          }));
          count = res.count || 0;
        } else if (mappingType === 'season') {
          const res = await seasonService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: SeasonInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            disabled: false,
            type: 'season' as const,
          }));
          count = res.count || 0;
        } else {
          const res = await contentService.fetch(new URLSearchParams(query));
          data = (res.results || []).map((item: ContentInterface) => ({
            id: item.id,
            key: item.id,
            name: item.title,
            disabled: false,
            type: 'content' as const,
          }));
          count = res.count || 0;
        }
        return {
          data,
          count,
        };
      },
      fetchTargetData: async query => {
        // 실제 등록된 아이템 불러오는 API
        // termId가 없거나, 음수(새로운 term)이거나, 새로운 term인 경우 API 호출하지 않음
        if (!termId || termId < 0 || isNewTerm) {
          return { data: [], count: 0 };
        }
        const res = await contractContentService.fetchTermItems(_, termId, query);
        return {
          data: (res.results || []).map(item => ({
            id: item.id,
            key: item.id,
            name: item.title,
            disabled: false,
            type: item.type,
          })),
          count: res.count || 0,
        };
      },
      createWithParentId: async (ids: number[]) => {
        // 등록 API 호출
        // termId가 없거나, 음수(새로운 term)이거나, 새로운 term인 경우 API 호출하지 않음
        if (!termId || termId < 0 || isNewTerm) return;
        await contractContentService.createItemsForContractTerm(_, termId, mappingType, ids);
      },
      deleteWithParentId: async (ids: number[]) => {
        // 해제 API 호출
        // termId가 없거나, 음수(새로운 term)이거나, 새로운 term인 경우 API 호출하지 않음
        if (!termId || termId < 0 || isNewTerm) return;
        await contractContentService.deleteItemsForContractTerm(_, termId, mappingType, ids);
      },
    }),
    [mappingType, termId, isNewTerm]
  );

  return (
    <div style={{ marginTop: 32 }}>
      <Typography.Title level={5} style={{ marginBottom: 12 }}>
        Contents Mapping
      </Typography.Title>

      {!termId || isNewTerm ? (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '8px',
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
            {isNewTerm
              ? 'Please save the Term first before Contents Mapping is available.'
              : 'Please select a Term before Contents Mapping is available.'}
          </Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: '14px', marginTop: '8px' }}>
            {isNewTerm
              ? 'Please enter Term information above and click the Save button.'
              : 'Please select a term from the Select Term dropdown.'}
          </Typography.Text>
        </div>
      ) : (
        <>
          <Radio.Group
            options={mappingTypes}
            onChange={e => setMappingType(e.target.value)}
            value={mappingType}
            optionType="button"
            style={{ marginBottom: 16 }}
          />
          <TransferComponent
            key={`${mappingType}-${termId}`}
            dataHandler={dataHandler}
            filterOption={filterOption}
            disabled={false}
            columns={columns}
          />
        </>
      )}
    </div>
  );
};

export default ContentsMapping;
